import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, FolderOpen, Layers, ArrowUpDown, Package } from 'lucide-react';
import { useViewConfig } from '../hooks/useViewConfig';
import { getProducts } from '../services/productService';
import { getCategoriesBySection } from '../services/categoryService';
import { getIconComponent } from '../utils/iconHelper';
import ShopSidebar from '../components/shop/ShopSidebar';
import ShopProductCard from '../components/shop/ShopProductCard';
import ShopSectionPills from '../components/shop/ShopSectionPills';
import ShopSkeleton from '../components/shop/ShopSkeleton';
import ShopEmptyState from '../components/shop/ShopEmptyState';
import ShopPagination from '../components/shop/ShopPagination';
import '../styles/shop.css';

const SECTION_COLORS = {
  "National Articles": "#10b981",
  "Premium & Imported": "#3b82f6",
  "Accessories": "#8b5cf6",
  "Fragrance & Grooming": "#f97316"
};

const SORT_OPTIONS = [
  { value: 'latest', label: 'Latest' },
  { value: 'popularity', label: 'Popularity' },
  { value: 'name-asc', label: 'Name A-Z' },
  { value: 'price-low', label: 'Price Low → High' },
  { value: 'price-high', label: 'Price High → Low' }
];

const getShortSectionName = (secName) => {
  if (secName === 'National Articles') return 'National';
  if (secName === 'Premium & Imported') return 'Premium';
  if (secName === 'Accessories') return 'Accessories';
  if (secName === 'Fragrance & Grooming') return 'Grooming';
  return secName;
};

const Shop = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const gridRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [mobilePage, setMobilePage] = useState(() => {
    return parseInt(searchParams.get('page'), 10) || 1;
  });
  
  // Extract state values from URL query parameters
  const search = searchParams.get('search') || '';
  const sort = searchParams.get('sort') || 'latest';
  const page = parseInt(searchParams.get('page'), 10) || 1;
  const activePage = isMobile ? mobilePage : page;

  // Multi-select sections
  const selectedSections = useMemo(() => {
    const list = [];
    const sectionsParam = searchParams.get('sections');
    if (sectionsParam) {
      sectionsParam.split(',').forEach(s => {
        const trimmed = s.trim();
        if (trimmed) list.push(trimmed);
      });
    }
    searchParams.getAll('sections').forEach(val => {
      val.split(',').forEach(s => {
        const trimmed = s.trim();
        if (trimmed) list.push(trimmed);
      });
    });
    const singular = searchParams.get('section');
    if (singular && singular !== 'All') {
      list.push(singular);
    }
    return Array.from(new Set(list));
  }, [searchParams]);

  // Multi-select category ObjectIds directly from URL
  const selectedCategoryIds = useMemo(() => {
    const list = [];
    const categoriesParam = searchParams.get('categories');
    if (categoriesParam) {
      categoriesParam.split(',').forEach(c => {
        const trimmed = c.trim();
        if (trimmed) list.push(trimmed);
      });
    }
    searchParams.getAll('categories').forEach(val => {
      val.split(',').forEach(c => {
        const trimmed = c.trim();
        if (trimmed) list.push(trimmed);
      });
    });
    const singular = searchParams.get('category');
    if (singular && singular !== 'All') {
      list.push(singular);
    }
    return Array.from(new Set(list));
  }, [searchParams]);

  // Fetch view configs
  const { data: sectionsConfig } = useViewConfig("HOME_SECTIONS");
  const { data: categoriesConfig } = useViewConfig("HOME_CATEGORIES");

  // State for all categories fetched once from database on mount
  const [allDbCategories, setAllDbCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  // State for query outputs
  const [products, setProducts] = useState([]);
  const [, setTotalProducts] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loadingProducts, setLoadingProducts] = useState(true);



  const loadMoreRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // On mobile, clean up 'page' parameter from URL on mount to ensure clean history
  useEffect(() => {
    if (isMobile && searchParams.has('page')) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('page');
      setSearchParams(nextParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mobile custom dropdown open state
  const [activeDropdown, setActiveDropdown] = useState(null); // 'category' | 'section' | 'sort' | null

  // Fetch all database categories once on mount
  useEffect(() => {
    let isMounted = true;
    const fetchAllCategories = async () => {
      try {
        setLoadingCategories(true);
        const data = await getCategoriesBySection(); // No arguments fetches all categories
        if (isMounted) {
          setAllDbCategories(data || []);
        }
      } catch (err) {
        console.error("Failed to fetch database categories:", err);
      } finally {
        if (isMounted) {
          setLoadingCategories(false);
        }
      }
    };
    fetchAllCategories();
    return () => {
      isMounted = false;
    };
  }, []);

  // Multi-select category names derived by mapping IDs to names in database categories
  const selectedCategories = useMemo(() => {
    const names = selectedCategoryIds.map(id => {
      const cat = allDbCategories.find(c => c._id === id);
      return cat ? cat.name : null;
    }).filter(Boolean);
    return Array.from(new Set(names));
  }, [selectedCategoryIds, allDbCategories]);

  // Sections list from view config
  const sectionsList = useMemo(() => {
    if (!sectionsConfig?.data?.sections) return [];
    return [...sectionsConfig.data.sections].sort(
      (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
    );
  }, [sectionsConfig]);

  // Resolve dynamic active categories (merged with view config, unfiltered by sections)
  const uniqueActiveCategories = useMemo(() => {
    const filteredDbCats = allDbCategories;

    const configCats = categoriesConfig?.data?.categories || categoriesConfig?.categories || [];
    
    const mergedCats = filteredDbCats.map(dbCat => {
      const match = configCats.find(c => {
        const titleClean = c.title?.toLowerCase().trim() || "";
        const nameClean = dbCat.name?.toLowerCase().trim() || "";
        return titleClean === nameClean;
      });

      return {
        _id: dbCat._id,
        name: dbCat.name,
        displayName: match?.title || dbCat.name,
        icon: match?.icon || 'Package',
        displayOrder: match?.displayOrder !== undefined ? match.displayOrder : 999,
        section: dbCat.section
      };
    });

    // Deduplicate by case-insensitive name
    const seenNames = new Set();
    const uniqueCats = [];
    
    mergedCats.sort((a, b) => a.displayOrder - b.displayOrder);

    for (const cat of mergedCats) {
      const normName = cat.name?.toLowerCase().trim() || "";
      if (!seenNames.has(normName)) {
        seenNames.add(normName);
        uniqueCats.push(cat);
      }
    }

    return uniqueCats;
  }, [allDbCategories, categoriesConfig]);

  // Selected category display label (for mobile button)
  const selectedCategoryLabel = useMemo(() => {
    if (selectedCategories.length === 0) return 'Category';
    const firstCat = selectedCategories[0];
    const match = uniqueActiveCategories.find(c => c.name === firstCat);
    const displayName = match ? match.displayName : firstCat;
    if (selectedCategories.length > 1) {
      return `${displayName} + ${selectedCategories.length - 1}`;
    }
    return displayName;
  }, [selectedCategories, uniqueActiveCategories]);

  // Selected section display label (for mobile button)
  const selectedSectionLabel = useMemo(() => {
    if (selectedSections.length === 0) return 'Section';
    const firstSec = getShortSectionName(selectedSections[0]);
    if (selectedSections.length > 1) {
      return `${firstSec} + ${selectedSections.length - 1}`;
    }
    return firstSec;
  }, [selectedSections]);

  // Resolve dynamic section color accents
  const activeColor = useMemo(() => {
    if (selectedSections.length === 0) return '#f97316';
    return SECTION_COLORS[selectedSections[0]] || '#f97316';
  }, [selectedSections]);

  const activeColorGlow = useMemo(() => {
    return activeColor.startsWith("#") ? `${activeColor}26` : "rgba(255, 255, 255, 0.15)";
  }, [activeColor]);

  // Page variables style binding
  const pageThemeStyle = {
    '--section-color': activeColor,
    '--section-color-glow': activeColorGlow,
  };

  const sectionsKey = selectedSections.join(',');
  const categoriesKey = selectedCategoryIds.join(',');



  // Fetch product list server-side on parameters modification
  useEffect(() => {
    let isMounted = true;

    const fetchProductsData = async () => {
      if (selectedCategoryIds.length > 0 && allDbCategories.length === 0) {
        return;
      }

      try {
        setLoadingProducts(true);

        const queryParams = {
          page: activePage,
          limit: 16,
          isAvailable: true
        };

        if (selectedSections.length > 0) {
          queryParams.sections = selectedSections;
        }
        if (selectedCategories.length > 0) {
          const queryCategoryIds = [];
          selectedCategories.forEach(name => {
            const matches = allDbCategories.filter(c => c.name?.toLowerCase().trim() === name?.toLowerCase().trim());
            matches.forEach(cat => {
              if (selectedSections.length === 0 || selectedSections.includes(cat.section)) {
                queryCategoryIds.push(cat._id);
              }
            });
          });
          if (queryCategoryIds.length > 0) {
            queryParams.categories = queryCategoryIds;
          }
        }
        if (search) queryParams.search = search;
        if (sort) queryParams.sort = sort;

        const data = await getProducts(queryParams);

        if (isMounted) {
          if (isMobile && activePage > 1) {
            setProducts(prev => {
              const existingIds = new Set(prev.map(p => p._id));
              const newProducts = (data.products || []).filter(p => !existingIds.has(p._id));
              return [...prev, ...newProducts];
            });
          } else {
            setProducts(data.products || []);
          }
          setTotalProducts(data.totalProducts || 0);
          setTotalPages(data.totalPages || 0);
        }
      } catch (err) {
        console.error("Failed to load products:", err);
      } finally {
        if (isMounted) {
          setLoadingProducts(false);
        }
      }
    };

    fetchProductsData();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionsKey, categoriesKey, search, sort, activePage, isMobile, allDbCategories]);

  // Infinite scroll trigger effect for mobile
  useEffect(() => {
    if (!isMobile || activePage >= totalPages || loadingProducts) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        updateFilters({
          page: activePage + 1
        });
      }
    }, {
      rootMargin: '150px'
    });

    const currentRef = loadMoreRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, activePage, totalPages, loadingProducts]);

  // Helper utility to merge and serialize search parameters
  const updateFilters = (newParams) => {
    const nextParams = new URLSearchParams(searchParams);

    // Sync sections (multi-select comma-separated)
    if (newParams.sections !== undefined) {
      nextParams.delete('sections');
      nextParams.delete('section');
      if (Array.isArray(newParams.sections) && newParams.sections.length > 0) {
        nextParams.set('sections', newParams.sections.join(','));
      }
    }

    // Sync categories (multi-select comma-separated)
    if (newParams.categories !== undefined) {
      nextParams.delete('categories');
      nextParams.delete('category');
      if (Array.isArray(newParams.categories) && newParams.categories.length > 0) {
        nextParams.set('categories', newParams.categories.join(','));
      }
    }

    // Scalar filters (search, sort, page)
    Object.keys(newParams).forEach((key) => {
      if (key === 'sections' || key === 'categories') return;
      const val = newParams[key];
      if (key === 'page') {
        if (isMobile) {
          setMobilePage(val);
          nextParams.delete('page');
          return;
        }
      }
      if (val === null || val === undefined || val === 'All' || val === '') {
        nextParams.delete(key);
      } else {
        nextParams.set(key, String(val));
      }
    });

    if (isMobile) {
      nextParams.delete('page');
    }

    setSearchParams(nextParams);
  };

  // State update handlers
  const handleSectionToggle = (sectionTitle) => {
    let nextSections;
    if (selectedSections.includes(sectionTitle)) {
      nextSections = selectedSections.filter(s => s !== sectionTitle);
    } else {
      nextSections = [...selectedSections, sectionTitle];
    }

    // Clean up selected category ObjectIds that no longer belong to active sections
    const nextCategoryIds = [];
    selectedCategories.forEach(name => {
      const matches = allDbCategories.filter(c => c.name?.toLowerCase().trim() === name?.toLowerCase().trim());
      matches.forEach(cat => {
        if (nextSections.length === 0 || nextSections.includes(cat.section)) {
          nextCategoryIds.push(cat._id);
        }
      });
    });

    updateFilters({
      sections: nextSections,
      categories: nextCategoryIds,
      page: 1
    });
  };

  const handleClearSections = () => {
    updateFilters({
      sections: [],
      page: 1
    });
  };

  const handleCategoryToggle = (categoryName) => {
    let nextCategoryNames;
    if (selectedCategories.includes(categoryName)) {
      nextCategoryNames = selectedCategories.filter(c => c !== categoryName);
    } else {
      nextCategoryNames = [...selectedCategories, categoryName];
    }

    // Map names back to IDs matching selected sections (or all sections if none selected)
    const nextCategoryIds = [];
    nextCategoryNames.forEach(name => {
      const matches = allDbCategories.filter(c => c.name?.toLowerCase().trim() === name?.toLowerCase().trim());
      matches.forEach(cat => {
        if (selectedSections.length === 0 || selectedSections.includes(cat.section)) {
          nextCategoryIds.push(cat._id);
        }
      });
    });

    updateFilters({
      categories: nextCategoryIds,
      page: 1
    });
  };

  // Mobile Select Dropdown Handlers
  const handleMobileCategorySelect = (categoryName) => {
    setActiveDropdown(null);
    if (categoryName === 'All') {
      updateFilters({ categories: [], page: 1 });
      return;
    }
    
    let nextCategoryNames;
    if (selectedCategories.includes(categoryName)) {
      nextCategoryNames = selectedCategories.filter(c => c !== categoryName);
    } else {
      nextCategoryNames = [...selectedCategories, categoryName];
    }

    const nextCategoryIds = [];
    nextCategoryNames.forEach(name => {
      const matches = allDbCategories.filter(c => c.name?.toLowerCase().trim() === name?.toLowerCase().trim());
      matches.forEach(cat => {
        if (selectedSections.length === 0 || selectedSections.includes(cat.section)) {
          nextCategoryIds.push(cat._id);
        }
      });
    });

    updateFilters({
      categories: nextCategoryIds,
      page: 1
    });
  };

  const handleMobileSectionSelect = (sectionName) => {
    setActiveDropdown(null);
    if (sectionName === 'All') {
      updateFilters({ sections: [], categories: [], page: 1 });
      return;
    }

    let nextSections;
    if (selectedSections.includes(sectionName)) {
      nextSections = selectedSections.filter(s => s !== sectionName);
    } else {
      nextSections = [...selectedSections, sectionName];
    }

    const nextCategoryIds = [];
    selectedCategories.forEach(name => {
      const matches = allDbCategories.filter(c => c.name?.toLowerCase().trim() === name?.toLowerCase().trim());
      matches.forEach(cat => {
        if (nextSections.length === 0 || nextSections.includes(cat.section)) {
          nextCategoryIds.push(cat._id);
        }
      });
    });

    updateFilters({
      sections: nextSections,
      categories: nextCategoryIds,
      page: 1
    });
  };

  const handleMobileSortSelect = (sortValue) => {
    setActiveDropdown(null);
    updateFilters({
      sort: sortValue,
      page: 1
    });
  };

  const handleSortChange = (newSort) => {
    updateFilters({
      sort: newSort,
      page: 1
    });
  };

  const handlePageChange = (newPage) => {
    updateFilters({
      page: newPage
    });
    if (gridRef.current) {
      const yOffset = -100;
      const element = gridRef.current;
      const y = element.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  };

  const handleClearFilters = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('sections');
    nextParams.delete('section');
    nextParams.delete('categories');
    nextParams.delete('category');
    nextParams.delete('sort');
    nextParams.delete('page');
    setSearchParams(nextParams);
    setMobilePage(1);
  };

  const isCategoryActive = selectedCategories.length > 0;
  const isSectionActive = selectedSections.length > 0;
  const isSortActive = sort !== 'latest';

  return (
    <div className="shop-page-body" style={pageThemeStyle}>

      {/* Mobile Sticky Header (Search Bar + Sticky Filter Row) */}
      <div className="mobile-sticky-header">
        {/* Sticky Filter Row */}
        <div className="mobile-sticky-filter-row">
          
          {/* Category Dropdown */}
          <div className="mobile-dropdown-wrapper category-wrapper">
            <button 
              className={`mobile-dropdown-btn ${isCategoryActive ? 'active-filter' : ''}`}
              onClick={() => setActiveDropdown(activeDropdown === 'category' ? null : 'category')}
            >
              <FolderOpen size={16} className="dropdown-btn-icon" />
              <span className="dropdown-btn-text">{selectedCategoryLabel}</span>
              <ChevronDown size={14} className="dropdown-btn-arrow" />
            </button>
            {activeDropdown === 'category' && (
              <div className="mobile-dropdown-menu">
                <div 
                  className={`mobile-dropdown-item ${selectedCategories.length === 0 ? 'selected-item' : ''}`}
                  onClick={() => handleMobileCategorySelect('All')}
                >
                  <Package size={14} className="item-icon" />
                  <span>All Categories</span>
                </div>
                {uniqueActiveCategories.map(cat => {
                  const isSelected = selectedCategories.includes(cat.name);
                  const IconComponent = getIconComponent(cat.icon);
                  return (
                    <div 
                      key={cat.name}
                      className={`mobile-dropdown-item ${isSelected ? 'selected-item' : ''}`}
                      onClick={() => handleMobileCategorySelect(cat.name)}
                    >
                      <IconComponent size={14} className="item-icon" />
                      <span>{cat.displayName || cat.name}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Section Dropdown */}
          <div className="mobile-dropdown-wrapper section-wrapper">
            <button 
              className={`mobile-dropdown-btn ${isSectionActive ? 'active-filter' : ''}`}
              onClick={() => setActiveDropdown(activeDropdown === 'section' ? null : 'section')}
            >
              <Layers size={16} className="dropdown-btn-icon" />
              <span className="dropdown-btn-text">{selectedSectionLabel}</span>
              <ChevronDown size={14} className="dropdown-btn-arrow" />
            </button>
            {activeDropdown === 'section' && (
              <div className="mobile-dropdown-menu">
                <div 
                  className={`mobile-dropdown-item ${selectedSections.length === 0 ? 'selected-item' : ''}`}
                  onClick={() => handleMobileSectionSelect('All')}
                >
                  <Layers size={14} className="item-icon" />
                  <span>All Sections</span>
                </div>
                {sectionsList.map(sec => {
                  const isSelected = selectedSections.includes(sec.title);
                  const IconComponent = getIconComponent(sec.icon);
                  return (
                    <div 
                      key={sec.title}
                      className={`mobile-dropdown-item ${isSelected ? 'selected-item' : ''}`}
                      onClick={() => handleMobileSectionSelect(sec.title)}
                    >
                      <IconComponent size={14} className="item-icon" />
                      <span>{sec.title}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sort Dropdown */}
          <div className="mobile-dropdown-wrapper sort-wrapper">
            <button 
              className={`mobile-dropdown-btn ${isSortActive ? 'active-filter' : ''}`}
              onClick={() => setActiveDropdown(activeDropdown === 'sort' ? null : 'sort')}
            >
              <ArrowUpDown size={16} className="dropdown-btn-icon" />
            </button>
            {activeDropdown === 'sort' && (
              <div className="mobile-dropdown-menu right-aligned">
                {SORT_OPTIONS.map(opt => {
                  const isSelected = sort === opt.value;
                  return (
                    <div 
                      key={opt.value}
                      className={`mobile-dropdown-item ${isSelected ? 'selected-item' : ''}`}
                      onClick={() => handleMobileSortSelect(opt.value)}
                    >
                      <span>{opt.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>

        {/* Fullscreen Backdrop overlay */}
        {activeDropdown && (
          <div className="mobile-dropdown-backdrop" onClick={() => setActiveDropdown(null)} />
        )}
      </div>

      {/* 4. Desktop Sidebar & Main Product Grid Layout split */}
      <div className="shop-main-layout" ref={gridRef}>
        <ShopSidebar
          categories={uniqueActiveCategories}
          loadingCategories={loadingCategories}
          activeCategories={selectedCategories}
          onCategoryToggle={handleCategoryToggle}
          onClearFilters={handleClearFilters}
        />

        <main className="shop-content-area">
          {/* Section navigation category pills */}
          <ShopSectionPills
            activeSections={selectedSections}
            onSectionToggle={handleSectionToggle}
            sections={sectionsList}
            sort={sort}
            onSortChange={handleSortChange}
            onClearSections={handleClearSections}
          />



          {/* Grid listing area */}
          {products.length === 0 && loadingProducts ? (
            <ShopSkeleton />
          ) : products.length === 0 ? (
            <ShopEmptyState onClearFilters={handleClearFilters} />
          ) : (
            <>
              <div className="shop-products-grid">
                {products.map((product) => (
                  <ShopProductCard key={product._id} product={product} />
                ))}
              </div>

              {/* Infinite scroll loader trigger (Mobile only) */}
              {isMobile && activePage < totalPages && (
                <div ref={loadMoreRef} className="mobile-infinite-scroll-trigger">
                  {loadingProducts && (
                    <div className="mobile-infinite-spinner-wrapper">
                      <div className="mobile-infinite-spinner" />
                      <span>Loading more products...</span>
                    </div>
                  )}
                </div>
              )}

              {/* Pagination controls (Desktop only) */}
              {!isMobile && (
                <ShopPagination
                  currentPage={activePage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </main>
      </div>

    </div>
  );
};

export default Shop;
